import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { initSync, clearAllData, forceSync, stopPeriodicSync, setSyncEnabled, setCloudBackupRunner } from '../lib/syncService';
import api from '../lib/api';
import lensApi from '../lib/lensApi';
import { initDB, migrateHighlightNotes, listDistinctUserIdsInBooks } from '../lib/localDB';
import { checkExtensionAvailable, getReaderTokenFromExtension, getExtensionToken } from '../lib/extension-auth';
import { parseJWT as parseJWTUtil, isJWTExpired } from '../lib/utils';
import { reportError } from '../lib/error-reporter';
import { API_BASE as AUTH_API_BASE } from '../lib/config';
import { setLanguage, ACTIVE_LANGUAGES } from '../lib/i18n';
import { normalizeUserPlanFields } from '../lib/planScope';

const AuthContext = createContext({});

// Storage keys
const TOKEN_KEY = 'easeverse_token';
const USER_KEY = 'easeverse_user';
const DEVICE_ID_KEY = 'easeverse_device_id';
const MISMATCH_DISMISSED_DATE_KEY = 'easeverse_mismatch_dismissed_date';
const FLAG_STORAGE_KEYS = {
  chunks_enabled: 'ev_feature_chunks',
  builtin_lens_enabled: 'ev_feature_builtin_lens',
  adaptive_display_enabled: 'ev_feature_adaptive',
  ipad_reader_beta: 'ev_feature_ipad_reader_beta',
  // Deep Reading sidebar — server-pushable per-account so gated testers /
  // rollout cohorts get it without DevTools (mirrors featureFlags.js key).
  deep_reading_enabled: 'ev_feature_deep_reading',
};

/** Fire-and-forget analytics event for Reader */
export function trackReaderEvent(event, metadata) {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return;
  fetch(`${AUTH_API_BASE}/analytics/event`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({ event, client: 'reader', metadata }),
  }).catch(() => {});
}

// Get or create a persistent device ID (shared with extension when available)
function getOrCreateDeviceId() {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c => (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16));
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
}

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);

  // lensApi is a separate singleton from `api` (different class instance,
  // independent authToken field). Without this sync, anything that goes
  // through lensApi.requestRehint / lensApi.streamQuickTranslate has no
  // Authorization header, and the backend treats those requests as
  // anonymous — burning through the 5/day anon bucket and 429ing the rest.
  // Hook the React token state so every login / logout / token-refresh
  // path propagates to both singletons in lockstep. Initial mount fires
  // once with token=null which is harmless (lensApi.authToken already null).
  useEffect(() => {
    lensApi.setAuthToken(token || null);
  }, [token]);
  const [loading, setLoading] = useState(true);
  /** Message from server when sync/me returns 401 (e.g. "logged in on another device"); show in UI, do not clear token until user signs out */
  const [sessionInvalidMessage, setSessionInvalidMessage] = useState(null);
  const [extensionAvailable, setExtensionAvailable] = useState(false);
  /** When extension user differs from Reader user, store the extension email for the prompt */
  const [accountMismatch, setAccountMismatch] = useState(null); // { extensionEmail: string } | null
  /** True while signOut() is in progress (forceSync + clearAllData). Consumed by SigningOutOverlay for full-screen UI feedback. */
  const [signingOut, setSigningOut] = useState(false);
  // Cloud auto-backup preference (paid). null/unset → default ON; mirrors the
  // server-backed users.auto_backup_enabled. Drives runCloudBackupScan gating.
  const [autoBackupEnabled, setAutoBackupEnabledState] = useState(true);
  /**
   * Holds the in-flight signOut() promise. Used as a reentry guard so
   * concurrent callers (e.g. double-clicked logout button) share the same
   * work instead of racing two forceSyncs + two clearAllDatas.
   * Cleared in signOut()'s finally.
   */
  const signingOutPromiseRef = useRef(null);

  // Use shared JWT utilities (no useCallback needed — pure functions)
  const parseJWT = parseJWTUtil;
  const isTokenExpired = isJWTExpired;

  /**
   * Initialize DB and sync (only for READER-paid users). Single place for plan
   * gating. `readerPlan` is the reader-effective plan (user.readerPlan) — a
   * Lens-only subscriber passes 'free' here even though their raw plan is paid.
   */
  const initDBAndSyncIfPaid = useCallback(async (authToken, onUnauth, readerPlan) => {
    const paid = !!readerPlan && readerPlan !== 'free';
    setSyncEnabled(paid);
    await initDB();
    await migrateHighlightNotes();
    if (paid) {
      // Background auto-backup of local-only books to the cloud. Ensure the api
      // singleton has the token (the 30s sync loop drives runCloudBackupScan
      // library-wide, not just on a reader page).
      api.setToken(authToken);
      // lensApi is a separate singleton from `api` — without this, chunk-hint
      // 👎 (sendFeedback → lensApi.requestRehint) lacks the Authorization
      // header and the backend treats the request as anonymous, dumping it
      // into the 5/day anon query bucket. Sync both at every token change.
      lensApi.setAuthToken(authToken);
      // Read the user's server-backed preference: null/unset → default ON (the
      // advertised paid feature); 0 → explicit opt-out.
      let on = true;
      try {
        const s = await api.getSettings();
        const v = s?.settings?.auto_backup_enabled;
        on = (v === null || v === undefined) ? true : !!v;
      } catch { /* keep default-on */ }
      setAutoBackupEnabledState(on);
      api.setAutoBackupEnabled(on);
      setCloudBackupRunner(() => api.runCloudBackupScan());
      await initSync(authToken, onUnauth);
    } else {
      // Not paid on the READER side (free user OR Lens-only subscription).
      // Stop any periodic sync a previous call started — covers the startup
      // cache race: a stale localStorage user said "paid" and initSync began,
      // then /me returned a scoped plan. setSyncEnabled(false) above already
      // no-ops future performSync calls; this kills the 30s loop itself.
      stopPeriodicSync();
      setAutoBackupEnabledState(false);
      api.setAutoBackupEnabled(false);
      setCloudBackupRunner(null);
    }
  }, []);

  /**
   * Toggle cloud auto-backup. Persists to the server (cross-device), flips the
   * runtime flag so the next sync tick honors it, and updates local state.
   */
  const setAutoBackup = useCallback(async (on) => {
    setAutoBackupEnabledState(!!on);
    api.setAutoBackupEnabled(!!on);
    try { await api.updateSettings({ auto_backup_enabled: !!on }); }
    catch (e) { reportError(e?.message || 'updateSettings(auto_backup) failed', { level: 'warn', metadata: { source: 'AuthContext.setAutoBackup' } }); }
  }, []);

  const syncFeatureFlags = useCallback(async (authToken) => {
    try {
      const response = await fetch(`${AUTH_API_BASE}/feature-flags`, {
        headers: { 'Authorization': `Bearer ${authToken}` },
      });
      if (!response.ok) return;
      const data = await response.json().catch(() => null);
      const flags = data?.flags;
      if (!flags || typeof flags !== 'object') return;
      Object.entries(FLAG_STORAGE_KEYS).forEach(([flagName, storageKey]) => {
        const value = flags[flagName];
        if (typeof value === 'boolean') {
          localStorage.setItem(storageKey, value ? 'true' : 'false');
        }
      });
    } catch {
      // Non-blocking: flag sync failure should not affect auth flow.
    }
  }, []);

  // Fetch user info from API (defined before useEffect that uses it)
  const fetchUserInfo = useCallback(async (authToken) => {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      const response = await fetch(`${AUTH_API_BASE}/me`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        },
        signal: controller.signal,
      });
      clearTimeout(timeout);

      if (response.ok) {
        const data = await response.json();
        // normalizeUserPlanFields derives the Reader-side gating fields
        // (readerPlan / readerPaid) and defaults planScope/product* to null.
        // When the backend is old and omits readerEffectivePlan, the local
        // fallback (computeReaderEffectivePlan) reproduces the server rule:
        // scope missing/combo/reader → plan, lens → 'free'.
        const userData = normalizeUserPlanFields({
          id: data.user_id,
          email: data.email,
          display_name: data.display_name || null,
          plan: data.plan,
          // Plan-scope fields (2026-07 unbundling; absent on old backends).
          planScope: data.planScope ?? null,
          readerEffectivePlan: data.readerEffectivePlan ?? undefined,
          productId: data.productId ?? null,
          productName: data.productName ?? null,
          iconUrl: data.iconUrl ?? null,
          cloudStorageGb: data.cloudStorageGb ?? 0,
          readerBookLimit: data.readerBookLimit ?? 3,
          // Monthly PDF-conversion page budget for this plan. 0 = PDF is not
          // part of the plan (Basic/Free — PDF moved to Pro/Max, 2026-07);
          // >0 or -1 = available. Gates the PDF upload flow in Library so
          // Basic subscribers don't walk the whole upload funnel into a 402.
          pdfPagesLimitMonthly: data.pdfPagesLimitMonthly ?? 0,
          // TTS source-language allowlist (null = unrestricted, pro/max). Gates pronunciation only.
          ttsSourceLangs: data.ttsSourceLangs ?? null,
          // Monthly chunk-splitting book limit (Basic 5, Pro/Max unlimited=-1).
          chunkBooksUsedMonthly: data.chunkBooksUsedMonthly ?? 0,
          chunkBooksLimitMonthly: data.chunkBooksLimitMonthly ?? 0,
          onboardingDay2Complete: !!data.onboardingDay2Complete,
        });
        // Paid (READER-side): complete first sync before setting user so Library
        // sees synced data (continue reading, shelves). If the cached-plan path
        // already started sync but /me now says reader-free (lens scope), this
        // call stops it (initDBAndSyncIfPaid's else branch).
        try {
          await initDBAndSyncIfPaid(authToken, (msg) => setSessionInvalidMessage(msg || null), userData.readerPlan);
        } catch (dbError) {
          console.error('[Auth] Failed to initialize DB:', dbError);
        }
        setUser(userData);
        localStorage.setItem(USER_KEY, JSON.stringify(userData));
        await syncFeatureFlags(authToken);
        return true;
      } else if (response.status === 401) {
        console.error('[Auth] Token invalid (401), signing out');
        try {
          const data = await response.json().catch(() => ({}));
          setSessionInvalidMessage(data.error || data.message || null);
        } catch {
          setSessionInvalidMessage(null);
        }
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setToken(null);
        setUser(null);
        Object.values(FLAG_STORAGE_KEYS).forEach((storageKey) => localStorage.removeItem(storageKey));
        return false;
      } else {
        console.error('[Auth] Failed to fetch user info:', response.status);
        return false;
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('[Auth] fetchUserInfo timed out after 10s');
        reportError('fetchUserInfo timed out after 10s', { level: 'error', stack: error.stack, metadata: { source: 'AuthContext.fetchUserInfo' } });
      } else {
        console.error('[Auth] Network error fetching user info:', error);
        reportError(error.message || 'Network error fetching user info', { level: 'error', stack: error.stack, metadata: { source: 'AuthContext.fetchUserInfo' } });
      }
      return false;
    }
  }, [initDBAndSyncIfPaid, syncFeatureFlags]);

  /**
   * Unified entry point for adopting a new JWT from any login flow.
   *
   * Called by: adoptToken, verifyOTP, loginWithExtension, loginWithGoogle,
   * confirmLogin. Centralises the account-switch detection that the individual
   * login entries used to skip — without this, user A's IndexedDB data would
   * leak into user B's session when B logs in on top of A.
   *
   * Account-switch detection is based on **IDB facts**, not just the old
   * localStorage token. The old-token-only check missed cases where:
   *   - localStorage was cleared manually (privacy mode, devtools)
   *   - token expired and got dropped but IDB data was retained
   *   - multiple accounts ever signed in on this device
   * See plan twinkly-jingling-pretzel.md — the sync 500 incident on
   * 2026-04-22 was caused exactly by this blind spot.
   *
   * Flow:
   *   1. Scan the `books` IDB store for distinct user_id values. Anything
   *      that isn't the incoming token's `sub` is residue from another
   *      account and must be cleared.
   *   2. For the residue that matches the old token's sub AND the old
   *      token isn't expired, forceSync() first to preserve the outgoing
   *      account's unsaved changes (best-effort).
   *   3. clearAllData({ userId: staleId }) removes each stale user's records
   *      from IndexedDB — including blobs stored by book id (book_files /
   *      book_covers), which have no user_id index.
   *   4. The new token is written to localStorage, fetchUserInfo reloads
   *      the profile + initialises DB/sync for the new account.
   *
   * Returns true on success, false if fetchUserInfo hit 401 (it clears token
   * itself on 401, so the caller should bail).
   */
  const switchToToken = useCallback(async (newToken) => {
    const newPayload = parseJWT(newToken);
    if (!newPayload?.sub) {
      console.error('[Auth] switchToToken: new token has no sub claim');
      return false;
    }
    const newUserId = newPayload.sub;

    const oldToken = localStorage.getItem(TOKEN_KEY);
    const oldPayload = oldToken ? parseJWT(oldToken) : null;

    // Scan IDB for any user_id !== newUserId. Covers all three residue paths
    // (old-token-gone, token-expired, multi-account-ever-here). Fails soft —
    // if the scan itself errors, we fall back to the legacy oldToken-only
    // path so login doesn't block on a diagnostic failure.
    let staleUserIds = [];
    try {
      const ids = await listDistinctUserIdsInBooks();
      staleUserIds = Array.from(ids).filter((uid) => uid && uid !== newUserId);
    } catch (e) {
      console.warn('[Auth] switchToToken: IDB scan failed, falling back to oldToken check:', e);
      if (oldPayload?.sub && oldPayload.sub !== newUserId) {
        staleUserIds = [oldPayload.sub];
      }
    }

    if (staleUserIds.length > 0) {
      console.info('[Auth] switchToToken: clearing IDB residue for previous account(s):', staleUserIds);

      // If the old token is still valid AND its sub is among the residue,
      // push its pending changes before wiping. Any other stale user_ids
      // (from even earlier accounts) have no valid token — we can't rescue
      // their unsaved work, but those changes were already unreachable
      // (the user has moved on) so the loss is acceptable.
      if (oldToken && oldPayload?.sub && staleUserIds.includes(oldPayload.sub) && !isTokenExpired(oldToken)) {
        try {
          await forceSync(oldToken);
        } catch (e) {
          reportError(
            `switchToToken forceSync failed: ${e?.message || String(e)}`,
            {
              level: 'warn',
              stack: e?.stack,
              metadata: { source: 'AuthContext.switchToToken.forceSync' },
            }
          );
        }
      }

      for (const staleId of staleUserIds) {
        try {
          await clearAllData({ userId: staleId });
        } catch (e) {
          console.error('[Auth] switchToToken clearAllData failed for', staleId, ':', e);
        }
      }
    }

    localStorage.setItem(TOKEN_KEY, newToken);
    setSessionInvalidMessage(null);
    const success = await fetchUserInfo(newToken);
    if (!success) {
      // fetchUserInfo already cleared token on 401
      return false;
    }
    setToken(newToken);
    return true;
  }, [parseJWT, isTokenExpired, fetchUserInfo]);

  /**
   * Helper: adopt a new token (from URL param, extension exchange, or stored
   * cache). Delegates to switchToToken for the cross-account path; adds a
   * cache-first optimisation for same-user token refreshes so the UI can
   * show the previous user profile instantly while fetchUserInfo runs in
   * the background.
   */
  const adoptToken = useCallback(async (newToken, storedToken, storedUser) => {
    const newPayload = parseJWT(newToken);
    const storedPayload = storedToken ? parseJWT(storedToken) : null;
    const isSameUser = storedPayload && newPayload?.sub === storedPayload?.sub;

    // Same-user path: no data clearing, show cached user immediately for fast UX.
    if (isSameUser && storedUser) {
      localStorage.setItem(TOKEN_KEY, newToken);
      setSessionInvalidMessage(null);
      setToken(newToken);
      try {
        // Normalize the cache: older builds wrote USER_KEY without the
        // plan-scope fields — missing fields get combo semantics (readerPlan
        // = plan), i.e. exactly the pre-unbundling behavior.
        const cachedUser = normalizeUserPlanFields(JSON.parse(storedUser));
        await initDBAndSyncIfPaid(newToken, (msg) => setSessionInvalidMessage(msg || null), cachedUser.readerPlan);
        setUser(cachedUser);
      } catch (e) {
        console.warn('[Auth] DB/sync init failed, fetching user info:', e);
      }
      // Refresh profile from server in background — display_name, plan changes, etc.
      await fetchUserInfo(newToken);
      return;
    }

    // Cross-user path (or no stored token at all): full switch.
    await switchToToken(newToken);
  }, [parseJWT, switchToToken, fetchUserInfo, initDBAndSyncIfPaid]);

  // Initialize auth state
  // Priority: localStorage token > extension exchange > unauthenticated
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem(TOKEN_KEY);
      const storedUser = localStorage.getItem(USER_KEY);

      // Step 1: Use stored token if valid (avoids redundant exchange calls)
      if (storedToken && !isTokenExpired(storedToken)) {
        setToken(storedToken);
        if (storedUser) {
          try {
            // Normalize the cache (older builds lack plan-scope fields →
            // combo semantics). If the cache says reader-paid, sync starts
            // now; the fetchUserInfo below re-evaluates with the fresh /me
            // and STOPS sync if the account is actually lens-scoped.
            const cachedUser = normalizeUserPlanFields(JSON.parse(storedUser));
            await initDBAndSyncIfPaid(storedToken, (msg) => setSessionInvalidMessage(msg || null), cachedUser.readerPlan);
            setUser(cachedUser);
          } catch (e) {
            console.warn('[Auth] DB/sync init failed, fetching user info:', e);
          }
        }
        await fetchUserInfo(storedToken);
        trackReaderEvent('session.start');
        setLoading(false);

        // Check if extension has a different account (detect mismatch on page load)
        // Also apply extension locale if available (avoids an extra GET /settings round-trip)
        try {
          const extAuth = await getExtensionToken();
          if (extAuth?.token) {
            setExtensionAvailable(true);
            if (extAuth.locale && ACTIVE_LANGUAGES.includes(extAuth.locale)) {
              setLanguage(extAuth.locale);
            }
            const extPayload = parseJWTUtil(extAuth.token);
            const currentPayload = parseJWTUtil(storedToken);
            if (extPayload?.sub && currentPayload?.sub && extPayload.sub !== currentPayload.sub) {
              const today = new Date().toDateString();
              if (localStorage.getItem(MISMATCH_DISMISSED_DATE_KEY) !== today) {
                const extLabel = extAuth.displayName || extAuth.email || extPayload.sub;
                setAccountMismatch({ extensionEmail: extLabel });
              }
            }
          }
        } catch {}
        return;
      }

      // Step 2: No stored token — check if extension is available (for the login page button)
      try {
        const available = await checkExtensionAvailable();
        setExtensionAvailable(available);
      } catch {
        setExtensionAvailable(false);
      }

      // Step 3: No valid token
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      setLoading(false);
    };

    // Safety: never stay stuck on loading for more than 15s
    const safetyTimeout = setTimeout(() => {
      setLoading((prev) => {
        if (prev) {
          console.warn('[Auth] Safety timeout: forcing loading=false after 15s');
          return false;
        }
        return prev;
      });
    }, 15000);

    initAuth().catch((err) => {
      console.error('[Auth] initAuth unexpected error:', err);
      reportError(err.message || 'initAuth unexpected error', { level: 'fatal', stack: err.stack, metadata: { source: 'AuthContext.initAuth' } });
      setLoading(false);
    });

    return () => clearTimeout(safetyTimeout);
  }, [isTokenExpired, fetchUserInfo, parseJWT, initDBAndSyncIfPaid, adoptToken]);

  // Re-sync with extension when the tab becomes visible (detect account mismatch + refresh plan)
  useEffect(() => {
    let lastRefreshTime = 0;
    const handleVisibility = async () => {
      if (document.visibilityState !== 'visible') return;

      // Refresh user profile (including plan) on tab focus, debounced to once per 30s
      const currentToken = localStorage.getItem(TOKEN_KEY);
      if (currentToken && Date.now() - lastRefreshTime > 30000) {
        lastRefreshTime = Date.now();
        fetchUserInfo(currentToken).catch(() => {});
      }

      const extAuth = await getExtensionToken();
      if (!extAuth?.token) return;

      // Sync locale from extension on tab focus (backup for easeverse:lang-change event)
      if (extAuth.locale && ACTIVE_LANGUAGES.includes(extAuth.locale)) {
        setLanguage(extAuth.locale);
      }

      const extPayload = parseJWT(extAuth.token);
      const currentPayload = currentToken ? parseJWT(currentToken) : null;

      if (extPayload?.sub && currentPayload?.sub && extPayload.sub !== currentPayload.sub) {
        const today = new Date().toDateString();
        if (localStorage.getItem(MISMATCH_DISMISSED_DATE_KEY) !== today) {
          const extLabel = extAuth.displayName || extAuth.email || extPayload.sub;
          setAccountMismatch({ extensionEmail: extLabel });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [parseJWT, fetchUserInfo]);

  /** User confirmed switching to the extension account */
  const confirmAccountSwitch = useCallback(async () => {
    setAccountMismatch(null);
    localStorage.removeItem(MISMATCH_DISMISSED_DATE_KEY);
    const currentToken = localStorage.getItem(TOKEN_KEY);
    const extResult = await getReaderTokenFromExtension();
    if (extResult?.token) {
      await adoptToken(extResult.token, currentToken, localStorage.getItem(USER_KEY));
    }
  }, [adoptToken]);

  /** User dismissed the account switch prompt — suppress for the rest of today */
  const dismissAccountSwitch = useCallback(() => {
    localStorage.setItem(MISMATCH_DISMISSED_DATE_KEY, new Date().toDateString());
    setAccountMismatch(null);
  }, []);

  // Send OTP to email
  const sendOTP = useCallback(async (email) => {
    try {
      const response = await fetch(`${AUTH_API_BASE}/auth/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send verification code');
      }

      return { success: true };
    } catch (error) {
      reportError(error.message || 'Failed to send verification code', { level: 'error', stack: error.stack, metadata: { source: 'AuthContext.sendOTP' } });
      return { error: error.message || 'Failed to send verification code' };
    }
  }, []);

  // Verify OTP and sign in (client_type: reader | collections so extension login doesn't kick web)
  // Returns { success } on login, { deviceLimit, devices, maxDevices, loginTicket } on 409, or { error } on failure
  const verifyOTP = useCallback(async (email, otp, opts = {}) => {
    try {
      const deviceId = getOrCreateDeviceId();
      const from = typeof window !== 'undefined' && window.location.search
        ? new URLSearchParams(window.location.search).get('from')
        : null;
      // Reader app always identifies as "reader" unless explicitly accessed for Collections
      const clientType = from === 'collections' ? 'collections' : 'reader';

      // Newsletter opt-in + locale hint: forwarded to backend so hybrid
      // double-opt-in logic can pick the right path (DE/AT/CH or de-locale
      // → confirm email; everyone else → single opt-in). Default false.
      const subscribeNewsletter = opts.subscribeNewsletter === true;
      const locale = typeof navigator !== 'undefined' ? navigator.language : null;

      const response = await fetch(`${AUTH_API_BASE}/auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          otp,
          device_id: deviceId,
          ...(clientType && { client_type: clientType }),
          subscribe_newsletter: subscribeNewsletter,
          ...(locale && { locale }),
        })
      });

      const data = await response.json();

      // Device limit reached — return device list for selection UI
      if (response.status === 409 && data.code === 'DEVICE_LIMIT_REACHED') {
        return {
          deviceLimit: true,
          devices: data.devices || [],
          maxDevices: data.max_devices || 0,
          loginTicket: data.login_ticket,
        };
      }

      if (!response.ok) {
        throw new Error(data.error || 'Invalid verification code');
      }

      // Routed through switchToToken so account switches (user A already
      // logged in, then user B verifies OTP) forceSync + clearAllData
      // instead of leaking A's data into B's session.
      const ok = await switchToToken(data.token);
      if (!ok) return { error: 'Invalid verification code' };
      return { success: true };
    } catch (error) {
      reportError(error.message || 'Failed to verify code', { level: 'error', stack: error.stack, metadata: { source: 'AuthContext.verifyOTP' } });
      return { error: error.message || 'Failed to verify code' };
    }
  }, [switchToToken]);

  // Log in via extension token exchange (triggered by user clicking the button)
  const loginWithExtension = useCallback(async () => {
    try {
      const extResult = await getReaderTokenFromExtension();
      // Reader device slot full — same eviction dialog as OTP/Google login.
      if (extResult?.deviceLimit) return extResult;
      if (extResult?.token && !isTokenExpired(extResult.token)) {
        // Routed through switchToToken for account-switch protection.
        const ok = await switchToToken(extResult.token);
        if (!ok) return { error: 'Extension login failed — please try email login' };
        return { success: true };
      }
      return { error: 'Extension login failed' };
    } catch (error) {
      reportError(error.message || 'Extension login failed', { level: 'error', stack: error.stack, metadata: { source: 'AuthContext.loginWithExtension' } });
      return { error: error.message || 'Extension login failed' };
    }
  }, [isTokenExpired, switchToToken]);

  // Log in via Google ID token (triggered by Google Sign-In button callback)
  // Returns { success }, { deviceLimit, devices, maxDevices, loginTicket }, or { error }
  const loginWithGoogle = useCallback(async (credential) => {
    try {
      const deviceId = getOrCreateDeviceId();
      const response = await fetch(`${AUTH_API_BASE}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential, device_id: deviceId, client_type: 'reader' })
      });

      const data = await response.json().catch(() => ({}));

      // Device limit reached — return device list for selection UI
      if (response.status === 409 && data.code === 'DEVICE_LIMIT_REACHED') {
        return {
          deviceLimit: true,
          devices: data.devices || [],
          maxDevices: data.max_devices || 0,
          loginTicket: data.login_ticket,
        };
      }

      if (!response.ok) {
        if (response.status >= 500) {
          reportError(data.error || `Google login server error ${response.status}`, { level: 'error', metadata: { source: 'AuthContext.loginWithGoogle', status: response.status } });
        }
        return { error: data.error || 'Google login failed' };
      }
      // Routed through switchToToken for account-switch protection.
      const ok = await switchToToken(data.token);
      if (!ok) return { error: 'Google login failed' };
      return { success: true };
    } catch (error) {
      reportError(error.message || 'Google login failed', { level: 'error', stack: error.stack, metadata: { source: 'AuthContext.loginWithGoogle' } });
      return { error: error.message || 'Google login failed' };
    }
  }, [switchToToken]);

  // Confirm login after device selection (second stage of two-stage flow)
  const confirmLogin = useCallback(async (loginTicket, evictDeviceId) => {
    try {
      const response = await fetch(`${AUTH_API_BASE}/auth/confirm-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login_ticket: loginTicket, evict_device_id: evictDeviceId })
      });

      const data = await response.json().catch(() => ({}));

      // If device list changed, return updated 409
      if (response.status === 409 && data.code === 'DEVICE_LIMIT_REACHED') {
        return {
          deviceLimit: true,
          devices: data.devices || [],
          maxDevices: data.max_devices || 0,
          loginTicket: data.login_ticket,
        };
      }

      if (!response.ok) {
        if (response.status >= 500) {
          reportError(data.error || `Confirm login server error ${response.status}`, { level: 'error', metadata: { source: 'AuthContext.confirmLogin', status: response.status } });
        }
        return { error: data.error || 'Login failed' };
      }

      // Routed through switchToToken for account-switch protection.
      const ok = await switchToToken(data.token);
      if (!ok) return { error: 'Login failed' };
      return { success: true };
    } catch (error) {
      reportError(error.message || 'Confirm login failed', { level: 'error', stack: error.stack, metadata: { source: 'AuthContext.confirmLogin' } });
      return { error: error.message || 'Confirm login failed' };
    }
  }, [switchToToken]);

  // Sign out. Protects unsynced paid-user data by forceSync'ing before wipe.
  //
  // Flow:
  //   1. Reentry guard via signingOutRef (not signingOut state — setState
  //      is async and wouldn't block a second click landing in the same
  //      microtask batch). First call wins; subsequent concurrent calls
  //      await the in-flight promise and resolve without re-running.
  //   2. setSigningOut(true) → SigningOutOverlay shows full-screen UI
  //   3. If paid user with a valid session → forceSync to push pending changes
  //      (best-effort: network failure does NOT block logout — the user asked
  //      to leave, and a hung logout button is worse than losing ~30s of data)
  //   4. clearAllData({ userId }) removes only this user's records (safer
  //      than the legacy blanket wipe — see syncStore.clearCurrentUserData)
  //   5. Clear localStorage, reset React state
  //   6. setSigningOut(false) → overlay hides; ProtectedRoute sees user=null
  //      and automatically navigates to /auth
  const signOut = useCallback(async () => {
    // Reentry guard: if a signOut is already running, return the same promise
    // so concurrent callers await the shared work instead of racing.
    if (signingOutPromiseRef.current) {
      return signingOutPromiseRef.current;
    }

    const work = (async () => {
      setSigningOut(true);
      try {
        const currentToken = localStorage.getItem(TOKEN_KEY);
        const currentUserId = user?.id || (currentToken ? parseJWT(currentToken)?.sub : null);
        // READER-side paid gate: forceSync-before-wipe only matters when cloud
        // sync exists for this account, which follows readerPaid (a Lens-only
        // subscriber has no Reader sync to flush).
        const isPaid = !!user?.readerPaid;

        // Skip forceSync when:
        //   - Free plan (no cloud sync)
        //   - No token (nothing to sync with)
        //   - sessionInvalidMessage set (token was 401'd — forceSync will just
        //     hit 401 again, waste of time and shows the user a delay they
        //     shouldn't have to wait for)
        if (isPaid && currentToken && !sessionInvalidMessage) {
          try {
            await forceSync(currentToken);
          } catch (e) {
            reportError(`signOut forceSync failed: ${e?.message || String(e)}`, {
              level: 'warn',
              stack: e?.stack,
              metadata: { source: 'AuthContext.signOut.forceSync' },
            });
          }
        }

        // Best-effort tell the backend to drop this device's KV session
        // (`session:devices:{uid}:reader`) so a leaked token can't replay
        // until natural TTL eviction.  Skipped when sessionInvalidMessage
        // is set (token already 401'd — pointless retry).  Same fire-and-
        // forget pattern as forceSync: network failure must not block the
        // user-visible logout flow.  Must happen BEFORE the localStorage
        // clear below — once TOKEN_KEY is gone the Authorization header
        // can't be built.
        if (currentToken && !sessionInvalidMessage) {
          try {
            await fetch(`${AUTH_API_BASE}/auth/logout`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${currentToken}` },
              keepalive: true,
            });
          } catch (e) {
            reportError(`signOut backend /auth/logout failed: ${e?.message || String(e)}`, {
              level: 'warn',
              stack: e?.stack,
              metadata: { source: 'AuthContext.signOut.backendLogout' },
            });
          }
        }

        // Clear only this user's records (not a blanket wipe).
        try {
          await clearAllData({ userId: currentUserId || undefined });
        } catch (e) {
          console.error('[Auth] signOut clearAllData failed:', e);
        }

        // Stop background cloud-backup for the signed-out user.
        try { setCloudBackupRunner(null); api.setAutoBackupEnabled(false); } catch { /* best-effort */ }

        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem(MISMATCH_DISMISSED_DATE_KEY);
        Object.values(FLAG_STORAGE_KEYS).forEach((k) => localStorage.removeItem(k));
        setSessionInvalidMessage(null);
        setToken(null);
        setUser(null);

        // Re-check extension availability so the Auth page shows the correct login UI
        checkExtensionAvailable()
          .then((available) => setExtensionAvailable(available))
          .catch((err) => {
            reportError(err.message || 'checkExtensionAvailable failed on signOut', {
              level: 'warn',
              stack: err?.stack,
              metadata: { source: 'AuthContext.signOut' },
            });
          });
      } finally {
        setSigningOut(false);
        signingOutPromiseRef.current = null;
      }
      return { error: null };
    })();

    signingOutPromiseRef.current = work;
    return work;
  }, [user, sessionInvalidMessage, parseJWT]);

  // Get current token for API calls
  const getToken = useCallback(() => {
    if (token && !isTokenExpired(token)) {
      return token;
    }
    return null;
  }, [token, isTokenExpired]);

  // Memoize isAuthenticated
  const isAuthenticated = useMemo(() => {
    return !!token && !isTokenExpired(token);
  }, [token, isTokenExpired]);

  const value = {
    user,
    token,
    loading,
    sendOTP,
    verifyOTP,
    confirmLogin,
    signOut,
    signingOut,
    getToken,
    isAuthenticated,
    sessionInvalidMessage,
    extensionAvailable,
    loginWithExtension,
    loginWithGoogle,
    accountMismatch,
    confirmAccountSwitch,
    dismissAccountSwitch,
    autoBackupEnabled,
    setAutoBackup,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
